import { useState } from 'react';
import { Draggable } from '@hello-pangea/dnd';
import { Trash, Tag, UserCirclePlus, Robot } from '@phosphor-icons/react';
import ConfirmDialog from './ConfirmDialog';
import { useT } from '../i18n/I18nContext';
import { labelColor } from '../lib/labels';
import { AGENT_BADGE_STYLES, isAgentOffline, isAgentRunning, useAgentClock } from '../lib/agent';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const PRIORITY_STYLES = {
  low: 'bg-zinc-100 text-zinc-600',
  medium: 'bg-blue-50 text-blue-700',
  high: 'bg-amber-50 text-amber-700',
  urgent: 'bg-red-50 text-red-700',
};

export default function TicketCard({ ticket, index, onClick, users, projectMembers, onDelete, onAssigneeChange, justLanded }) {
  const t = useT();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // ``users`` is the full list so a card still shows the name + color of an
  // assignee who has since been removed from the project. ``projectMembers``
  // is what the assignee picker offers — preventing re-assignment to outsiders.
  const assignableUsers = projectMembers ?? users ?? [];
  const assignee = users?.find((u) => u.id === ticket.assignee_id);
  const initials = assignee
    ? assignee.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()
    : ticket.assignee_id
      ? '?'
      : '';

  const agent = ticket.agent;
  const agentRunning = isAgentRunning(agent);
  useAgentClock(agentRunning);
  const agentOffline = isAgentOffline(agent);

  const handleConfirmDelete = async () => {
    if (!onDelete) return;
    setDeleting(true);
    try {
      await onDelete(ticket.id);
    } finally {
      setDeleting(false);
    }
  };

  const handleAssign = (userId) => {
    if (!onAssigneeChange) return;
    const next = userId === 'none' ? null : userId;
    if (next === (ticket.assignee_id ?? null)) return;
    onAssigneeChange(ticket.id, next);
  };

  return (
    <Draggable draggableId={ticket.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          data-testid={`ticket-card-${ticket.id}`}
          onClick={onClick}
          className={`group relative bg-white border border-zinc-200 rounded-md px-3 pt-2 pb-2 cursor-pointer hover:border-zinc-400 hover:-translate-y-px transition-all flex flex-col h-[116px] flex-shrink-0 overflow-hidden ${
            snapshot.isDragging ? 'shadow-lg border-zinc-400' : ''
          } ${justLanded ? 'review-wiggle' : ''}`}
        >
          <ConfirmDialog
            open={confirmOpen}
            onOpenChange={setConfirmOpen}
            title={t('ticketCard.deleteConfirmTitle')}
            description={t('ticketCard.deleteConfirmDescription', { title: `${ticket.title.slice(0, 120)}${ticket.title.length > 120 ? '...' : ''}` })}
            confirmLabel={t('ticketCard.deleteConfirmBtn')}
            cancelLabel={t('common.cancel')}
            variant="destructive"
            loading={deleting}
            onConfirm={handleConfirmDelete}
          />

          <div className="flex items-center justify-between gap-2 mb-1">
            <span
              className="inline-flex items-center gap-0.5 min-w-0 max-w-full text-[10px] font-mono text-zinc-800 tracking-wide"
              title={t('ticketCard.ticketNumberTitle', { number: ticket.number })}
              aria-label={t('ticketCard.ticketNumberTitle', { number: ticket.number })}
              data-testid={`ticket-card-number-${ticket.id}`}
            >
              <Tag
                size={12}
                weight="bold"
                className="shrink-0 text-teal-600 transition-transform duration-300 ease-in-out origin-[15%_55%] group-hover:-rotate-12"
                aria-hidden
              />
              <span className="truncate tabular-nums">{ticket.number}</span>
            </span>
            <div className="flex items-center gap-1 flex-shrink-0">
              {onDelete && (
                <button
                  type="button"
                  data-testid={`ticket-card-delete-${ticket.id}`}
                  title={t('ticketCard.deleteTitle')}
                  className="p-1 rounded-sm text-zinc-400 hover:text-red-600 hover:bg-red-50 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmOpen(true);
                  }}
                >
                  <Trash size={14} weight="bold" />
                </button>
              )}
              <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm whitespace-nowrap ${PRIORITY_STYLES[ticket.priority]}`}>
                {t(`priority.${ticket.priority}`)}
              </span>
            </div>
          </div>

          <h4 className="text-sm font-semibold leading-snug text-zinc-900 truncate mb-1.5">{ticket.title}</h4>

          {agent ? (
            <div
              className="flex items-center gap-1.5 mb-2 min-h-[16px] min-w-0"
              data-testid={`ticket-card-agent-${ticket.id}`}
              title={[agentOffline ? t('agent.offline') : t(`agent.state.${agent.state}`), agent.step].filter(Boolean).join(' — ')}
            >
              <span
                aria-label={agentOffline ? t('agent.offline') : t(`agent.state.${agent.state}`)}
                className={`inline-flex items-center shrink-0 p-0.5 border rounded-sm ${
                  agentOffline ? 'bg-zinc-100 text-zinc-500 border-zinc-200' : AGENT_BADGE_STYLES[agent.state]
                }`}
              >
                <Robot size={12} weight="bold" />
              </span>
              {agent.step && (
                <span className="text-[11px] text-zinc-500 truncate">{agent.step}</span>
              )}
            </div>
          ) : (
            <p className="text-xs text-zinc-500 truncate mb-2 min-h-[16px]">
              {(ticket.description || '').replace(/!\[[^\]]*\]\([^)]*\)/g, '').trim() || ' '}
            </p>
          )}

          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0 flex-1 overflow-hidden">
              {ticket.labels?.length > 0 && ticket.labels.map((l) => (
                <span
                  key={l}
                  title={l}
                  className={`text-[10px] font-mono px-1.5 py-0.5 border rounded-sm truncate max-w-[100px] flex-shrink-0 ${labelColor(l)}`}
                >
                  {l}
                </span>
              ))}
            </div>
            {onAssigneeChange && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    data-testid={`ticket-assign-trigger-${ticket.id}`}
                    title={assignee ? t('ticketCard.assignedTo', { name: assignee.name }) : t('ticketCard.assignSomeone')}
                    style={
                      assignee?.color
                        ? { backgroundColor: assignee.color }
                        : undefined
                    }
                    className={`flex-shrink-0 rounded-full flex items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-zinc-400 ${
                      assignee || ticket.assignee_id
                        ? `w-6 h-6 text-white text-[11px] font-semibold${assignee?.color ? '' : ' bg-zinc-500'}`
                        : 'w-6 h-6 border border-dashed border-zinc-300 text-zinc-400 hover:border-zinc-400 hover:text-zinc-600 bg-white'
                    }`}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {assignee || ticket.assignee_id ? initials || '?' : <UserCirclePlus size={16} weight="bold" />}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="z-[100] w-52 rounded-sm">
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                    {t('ticketCard.assignTo')}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuRadioGroup
                    value={ticket.assignee_id || 'none'}
                    onValueChange={handleAssign}
                  >
                    <DropdownMenuRadioItem value="none" className="rounded-sm text-xs">
                      {t('newTicket.unassigned')}
                    </DropdownMenuRadioItem>
                    {assignableUsers.map((u) => (
                      <DropdownMenuRadioItem key={u.id} value={u.id} className="rounded-sm text-xs">
                        {u.name}
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );
}
